/**
 * API Response Normalizer
 *
 * Utility functions to normalize API responses to consistent formats
 * Handles both direct arrays/objects and wrapped responses
 */

/**
 * Normalize an array response
 * Handles: direct array, { key: [...] }, or null/undefined
 */
export function normalizeArray<T>(
  response: unknown,
  key?: string
): T[] {
  // Handle null/undefined
  if (!response) {
    return [];
  }

  // Handle direct array
  if (Array.isArray(response)) {
    return response;
  }

  // Handle wrapped object { key: [...] }
  if (key && typeof response === 'object' && response !== null) {
    const obj = response as Record<string, unknown>;
    if (key in obj && Array.isArray(obj[key])) {
      return obj[key] as T[];
    }
  }

  // Fallback: try to find any array property
  if (typeof response === 'object' && response !== null) {
    const obj = response as Record<string, unknown>;
    for (const value of Object.values(obj)) {
      if (Array.isArray(value)) {
        return value as T[];
      }
    }
  }

  return [];
}

/**
 * Normalize an object response
 * Handles: direct object, { key: {...} }, or null/undefined
 */
export function normalizeObject<T>(
  response: unknown,
  key?: string
): T | null {
  // Handle null/undefined
  if (!response) {
    return null;
  }

  // Handle direct object (if it matches expected structure)
  if (typeof response === 'object' && response !== null && !Array.isArray(response)) {
    // If key is provided, try to extract from nested object
    if (key) {
      const obj = response as Record<string, unknown>;
      if (key in obj && typeof obj[key] === 'object' && obj[key] !== null) {
        return obj[key] as T;
      }
    }
    // Otherwise return as-is
    return response as T;
  }

  return null;
}

/**
 * Normalize a paginated response
 */
export function normalizePaginatedResponse<T>(
  response: unknown,
  dataKey: string = 'data'
): {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
} {
  if (!response || typeof response !== 'object') {
    return {
      data: [],
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0,
    };
  }

  const obj = response as Record<string, unknown>;

  return {
    data: normalizeArray<T>(obj[dataKey] || obj.data || obj.items || []),
    page: (typeof obj.page === 'number' ? obj.page : 1),
    pageSize: (typeof obj.pageSize === 'number' ? obj.pageSize : 20),
    total: (typeof obj.total === 'number' ? obj.total : 0),
    totalPages: (typeof obj.totalPages === 'number' ? obj.totalPages : 0),
  };
}
